import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Badge,
  useMediaQuery,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccessTime as TimeIcon,
  Assignment as TaskIcon,
  Folder as ProjectIcon,
  People as PeopleIcon,
  Assessment as ReportIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  FormatListBulleted as TaskListIcon,
  Work as WorkIcon,
  Settings as SettingsIcon,
  AccountBalance as FinanceIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ChatContext } from '../contexts/ChatContext';
import { useTheme } from '@mui/material/styles';
import GlobalSearchDialog from './search/GlobalSearchDialog';
import { roleHome } from '../utils/roleHome';

const Header = () => {
  const { user, logout, isAdmin, isClient } = useAuth();
  const { isChatOpen, setIsChatOpen, unreadCounts } = useContext(ChatContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K öffnet die Recherche von jeder Seite aus.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSubmenuOpen = (event, itemPath) => {
    setSubmenuAnchorEl(prev => ({ ...prev, [itemPath]: event.currentTarget }));
  };

  const handleSubmenuClose = (itemPath) => {
    setSubmenuAnchorEl(prev => ({ ...prev, [itemPath]: null }));
  };

  if (!user) return null;

  const clientUser = isClient && isClient();

  // Vereinfachte Navigation - 4 Hauptbereiche statt 8
  // clientHidden: ausgeblendet für externe Kunden-User
  const menuItems = [
    {
      text: 'Projekte',
      path: '/pm',
      icon: <ProjectIcon />,
      admin: false,
      description: 'Projektmanagement'
    },
    {
      text: 'Arbeit',
      path: '/tasks',
      icon: <WorkIcon />,
      admin: false,
      clientHidden: true,
      description: 'Tasks & Liste',
      submenu: [
        { text: 'Task erstellen', path: '/tasks', icon: <TaskIcon /> },
        { text: 'Meine Taskliste', path: '/my-tasklist', icon: <TaskListIcon /> },
        { text: 'Alle Tasks', path: '/all-tasks', icon: <TaskIcon /> },
      ]
    },
    {
      text: 'Zeit',
      path: '/timetracking',
      icon: <TimeIcon />,
      admin: false,
      clientHidden: true,
      description: 'Zeiterfassung & Reports',
      submenu: [
        { text: 'Zeit erfassen', path: '/timetracking', icon: <TimeIcon /> },
        { text: 'Reports', path: '/reports', icon: <ReportIcon /> },
      ]
    },
    {
      text: 'Finanzen',
      path: '/invoices',
      icon: <FinanceIcon />,
      admin: true,
      clientHidden: true,
      description: 'Rechnungen & Kunden',
      submenu: [
        { text: 'Rechnungen', path: '/invoices', icon: <ReceiptIcon /> },
        { text: 'Kunden', path: '/customers', icon: <BusinessIcon /> },
      ]
    },
    {
      text: 'Admin',
      path: '/projects',
      icon: <SettingsIcon />,
      admin: true,
      clientHidden: true,
      description: 'Projekte & Benutzer',
      submenu: [
        { text: 'Projekte', path: '/projects', icon: <ProjectIcon /> },
        { text: 'Benutzer', path: '/users', icon: <PeopleIcon /> },
      ]
    },
  ];

  const visibleMenuItems = menuItems.filter((item) => {
    if (item.admin && !isAdmin()) return false;
    if (item.clientHidden && clientUser) return false;
    return true;
  });

  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2, bgcolor: 'primary.main', cursor: 'pointer' }}
           onClick={() => { navigate(roleHome(user)); setMobileOpen(false); }}
           title="Zum Dashboard">
        <img
          src="/redorbit_logo.svg"
          alt="RedOrbit"
          style={{ height: '30px', width: 'auto' }}
        />
      </Box>
      <Divider />
      <List>
        {visibleMenuItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.submenu && item.submenu.some(sub => location.pathname === sub.path));

          return (
            <Box key={item.path}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                selected={isActive}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.description}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
              {item.submenu && (
                <List component="div" disablePadding>
                  {item.submenu.map((subItem) => (
                    <ListItemButton
                      key={subItem.path}
                      sx={{ pl: 4 }}
                      onClick={() => {
                        navigate(subItem.path);
                        setMobileOpen(false);
                      }}
                      selected={location.pathname === subItem.path}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: location.pathname === subItem.path ? 'primary.main' : 'inherit' }}>
                        {subItem.icon}
                      </ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItemButton onClick={() => { navigate('/account'); setMobileOpen(false); }}>
          <ListItemIcon>
            <AccountIcon />
          </ListItemIcon>
          <ListItemText primary="Mein Konto" />
        </ListItemButton>
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={2}
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 } }}>
            {/* Left side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {isMobile && (
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ color: 'white' }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => navigate(roleHome(user))}
                title="Zum Dashboard"
              >
                {/* Original RedOrbit Logo – führt zur Startseite (Dashboard bzw. Projekte für Kunden) */}
                <img
                  src="/redorbit_logo.svg"
                  alt="RedOrbit"
                  style={{
                    height: '24px',
                    width: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Box>

            {/* Center - Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {visibleMenuItems.map((item) => {
                  const isActive = location.pathname === item.path ||
                    (item.submenu && item.submenu.some(sub => location.pathname === sub.path));
                  const hasSubmenu = item.submenu && item.submenu.length > 0;

                  return (
                    <Box key={item.path}>
                      <Button
                        onClick={(e) => {
                          if (hasSubmenu) {
                            handleSubmenuOpen(e, item.path);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        startIcon={item.icon}
                        sx={{
                          color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
                          fontWeight: isActive ? 600 : 500,
                          px: 3,
                          py: 1,
                          borderBottom: isActive ? '2px solid white' : '2px solid transparent',
                          borderRadius: 0,
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                          },
                        }}
                      >
                        {item.text}
                      </Button>

                      {hasSubmenu && (
                        <Menu
                          anchorEl={submenuAnchorEl[item.path]}
                          open={Boolean(submenuAnchorEl[item.path])}
                          onClose={() => handleSubmenuClose(item.path)}
                          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                          sx={{
                            zIndex: 1301,
                          }}
                          PaperProps={{
                            sx: {
                              mt: 0.5,
                              minWidth: 180,
                              borderRadius: 2,
                            },
                          }}
                        >
                          {item.submenu.map((subItem) => (
                            <MenuItem
                              key={subItem.path}
                              onClick={() => {
                                navigate(subItem.path);
                                handleSubmenuClose(item.path);
                              }}
                              selected={location.pathname === subItem.path}
                              sx={{
                                py: 1.5,
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(30, 58, 95, 0.08)',
                                  '&:hover': {
                                    bgcolor: 'rgba(30, 58, 95, 0.12)',
                                  },
                                },
                              }}
                            >
                              <ListItemIcon sx={{ color: location.pathname === subItem.path ? 'primary.main' : 'inherit' }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText>{subItem.text}</ListItemText>
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Right side - User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Ticket-Recherche (Cmd/Ctrl+K) */}
              <IconButton
                onClick={() => setSearchOpen(true)}
                size="large"
                sx={{ color: 'white' }}
                title="Tickets durchsuchen (⌘K)"
                aria-label="Tickets durchsuchen"
              >
                <SearchIcon />
              </IconButton>

              {/* Chat Icon with Unread Badge - nur für interne User */}
              {!clientUser && (
                <IconButton
                  onClick={() => setIsChatOpen(!isChatOpen)}
                  size="large"
                  sx={{ color: 'white' }}
                >
                  <Badge badgeContent={totalUnread} color="error">
                    <ChatIcon />
                  </Badge>
                </IconButton>
              )}

              {isAdmin() && (
                <Chip
                  label="Admin"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                    color: 'white',
                    fontWeight: 600,
                    display: { xs: 'none', sm: 'flex' },
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
              )}
              <IconButton onClick={handleMenuOpen} size="large">
                <Avatar
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    fontWeight: 600,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    borderRadius: 2,
                  },
                }}
              >
                <MenuItem disabled>
                  <Box>
                    <Box sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {user.username}
                    </Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {user.email}
                    </Box>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { navigate('/account'); handleMenuClose(); }}>
                  <ListItemIcon>
                    <AccountIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mein Konto</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Abmelden</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </Drawer>

      <GlobalSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
